"use client";

import React from "react";
import styled from "styled-components";
import { logToSentry } from "@/lib/PlatformData/platformDataErrorTracking";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";
import { dateListOffer } from "@/lib/Types/DateListResponse/DateListResponse";
import { getOfferIssues } from "./getOfferIssues";

interface Props {
  children: React.ReactNode;
  offer: dateListOffer;
  envConfig: EnvConfig;
  debugMode?: boolean;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
}

const DebugFallback = styled.div(({ theme }) => ({
  padding: theme.kallisto.space.space200,
  border: `2px dashed ${theme.kallisto.color.common.semantic.error[500]}`,
  borderRadius: theme.kallisto.border.radius.radius100,
  backgroundColor: theme.kallisto.color.common.semantic.error[50],
  color: theme.kallisto.color.text.static.textError,
  fontFamily: "monospace",
  fontSize: "12px",
  display: "flex",
  flexDirection: "column",
  gap: theme.kallisto.space.space100,
  wordBreak: "break-all",
}));

const DebugLink = styled.a(({ theme }) => ({
  color: theme.kallisto.color.text.static.textSecondary,
  lineHeight: "1.5",
}));

class OfferListItemErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, errorMessage: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { offer, envConfig } = this.props;
    const issues = getOfferIssues(offer);

    logToSentry(
      error,
      [["component", "OfferListItem"]],
      {
        offerId: offer.offerId,
        objectCode: offer.objectCode,
        issues,
        componentStack: errorInfo.componentStack,
      },
    );

    sendKibanaLog({
      envConfig,
      message: "OfferListItem render error",
      category: "error",
      jsonMessage: {
        offerId: offer.offerId,
        objectCode: offer.objectCode,
        error: error.message,
        issues,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.debugMode) {
        const { offer } = this.props;
        const issues = getOfferIssues(offer);
        return (
          <DebugFallback>
            <strong>Broken offer: {offer.offerId}</strong>
            <span>objectCode: {offer.objectCode}</span>
            {issues.length > 0 && (
              <>
                <strong>Detected issues:</strong>
                {issues.map((issue) => (
                  <span key={issue}>{issue}</span>
                ))}
              </>
            )}
            <strong>Error:</strong>
            <span>{this.state.errorMessage}</span>
            {offer.lastminuteHotelDetailUrl && (
              <>
                <strong>Hotel Detail link (lastminuteHotelDetailUrl):</strong>
                <DebugLink
                  href={offer.lastminuteHotelDetailUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {offer.lastminuteHotelDetailUrl}
                </DebugLink>
              </>
            )}
          </DebugFallback>
        );
      }
      return null;
    }
    return this.props.children;
  }
}

export default OfferListItemErrorBoundary;
